/* eslint-disable import/max-dependencies */
import { useMutation, useQuery } from '@apollo/client'
import { BodyText, Button, Col, HeadingText, Row } from '@paypalcorp/pp-react'
import { navigate } from '@reach/router'
import { merge } from 'lodash-es'
import AddressCard from 'ppbl/components/AddressCard'
import SpinnerAdapter from 'ppbl/components/SpinnerAdapter'
import InfoText from 'ppbl/components/InfoText'
import { updateApplicationCachedData } from 'ppbl/graphql/__utils__'
import {
  updateApplicationMutation,
  normalizeAddressMutation,
} from 'ppbl/graphql/mutations'
import { getCreditApplicationDataQuery } from 'ppbl/graphql/queries'
import serverData from 'ppbl/utils/server-data'
import { useApplicationContext } from 'ppbl/hooks'
import clientLogger from 'ppbl/utils/client-logger'
import { trackClick, trackPage } from 'ppbl/utils/fpti-helper'
import { FPTI_EVENTS } from 'ppbl/utils/fptiEventNames'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import { santizeInputFields } from 'ppbl/utils/string-helper'
import { scrollOnErrors } from 'ppbl/utils/validation-helper'
import React, { useEffect, useRef, useState } from 'react'
import { Field, Form } from 'react-final-form'
import {
  scrollToTopOnContinue,
  getApplicationFieldsData,
  updateApplicationPayloadGeneratorPage2,
  handleGraphqlError,
} from 'ppbl/screens/__utils__'
import { applicationPage2Fields } from '../applicationFieldMapper'
import './styles.css'
import { cloneAndMask } from 'ppbl/utils/data-masker'
import createDecorator from 'ppbl/utils/form/decorator'
import AddressConfirmationDialog from 'ppbl/components/AddressConfimationDialog'
import { AddressType } from '../../../types'
import { addressDialogConstants } from 'ppbl/utils/constants'
import {
  WorldReady,
  AddressFormat,
  PhoneNumberFormat,
} from '@paypalcorp/worldready'
import PreApprovalBanner from 'ppbl/components/PreApprovalBanner'

type Props = {
  applicationType: string
  creditApplicationLoading: boolean
}

const fptiData = {
  page: 'applicationPage2',
  event_name: FPTI_EVENTS.PAGE.PAGE2,
}

const ApplicationPage2: React.FC<Props> = ({
  applicationType,
  creditApplicationLoading,
}) => {
  const { data, updateApplicationContext } = useApplicationContext()
  const [isAddressNormalizationDialog, setIsAddressNormalizationDialog] =
    useState<boolean>(false)
  const [suggestedAddress, setSuggestedAddress] = useState()
  const [userAddress, setUserAddress] = useState<AddressType>()
  const [applicationFormData, setApplicationFormData] = useState()

  useEffect(() => {
    clientLogger.info(
      'Application Page 2 is loaded - Application ID:',
      data?.applicationMetaData?.applicationId,
    )
    trackPage(
      merge(fptiData, {
        applicationId: data?.applicationMetaData?.applicationId,
      }),
    )
  }, [])

  const isPaypalApplication = applicationType === 'paypal'
  const renewalToken = serverData.startParams?.renewalToken

  const { data: cachedApplicationData } = useQuery(
    getCreditApplicationDataQuery,
  )

  const [changeApplicationFields, setChangeApplicationFields] = useState(
    cachedApplicationData.homeAddress1 === null ||
      cachedApplicationData.city === null ||
      cachedApplicationData.state === null ||
      cachedApplicationData.zip === null,
  )

  const [applicationPageFields, setApplicationPageFields] = useState(
    changeApplicationFields ? applicationPage2Fields : [],
  )

  const [updateApplication, { loading }] = useMutation(
    updateApplicationMutation,
    {
      onCompleted(response) {
        clientLogger.info(
          'Response received after updating personal information in application page 2',
          cloneAndMask(response),
        )
        // push the response.data.credit.businessCredit.createApplication.creditApplication into context
        sessionStorage.setItem('mostRecentCompletedPage', 'applicationPage2')
        updateApplicationContext({
          applicationMetaData: {
            ...data.applicationMetaData,
            mostRecentCompletedPage: 'applicationPage2',
          },
        })
        navigate(
          `/merchant-lending/loanbuilder/application/us/${applicationType}/applicationPage3`,
        )
      },
      onError: (error) => {
        handleGraphqlError(
          error,
          'Error encountered while updating personal information in application page 2',
          'ApplicationPage 2 - updateApplicationMutation',
          data?.applicationMetaData?.applicationId,
          renewalToken,
          data?.applicationMetaData?.legalBusinessName,
          applicationType,
        )
      },
    },
  )

  const [generateNormalizedAddress, { loading: addressNormalizationLoading }] =
    useMutation(normalizeAddressMutation, {
      onCompleted(response) {
        clientLogger.info(
          'Response received of normalized address in application page 2',
          cloneAndMask(response),
        )
        const normalizedResponse =
          response?.credit?.businessCredit?.normalizeAddress
        if (normalizedResponse?.status === 'VERIFIED') {
          updateApplicationCachedData(
            applicationFormData,
            cachedApplicationData,
          )
          updateApplication({
            variables: {
              updateInput: updateApplicationPayloadGeneratorPage2(
                applicationFormData,
                data.applicationMetaData.applicationId,
              ),
            },
          })
        } else {
          setIsAddressNormalizationDialog(true)
          if (normalizedResponse?.address) {
            setSuggestedAddress(normalizedResponse)
          }
        }
      },
      onError: (error) => {
        handleGraphqlError(
          error,
          'Error encountered while calling address normalization in application page 2',
          'ApplicationPage 2 - normalizeAddressMutation',
          data?.applicationMetaData?.applicationId,
          renewalToken,
          data?.applicationMetaData?.legalBusinessName,
          applicationType,
        )
      },
    })

  const handleAddressOnChange = () => {
    trackClick(
      merge(fptiData, {
        component: 'applicationPage2_change_button',
        link: 'applicationPage2_change_button',
        applicationId: data?.applicationMetaData?.applicationId,
        event_name: FPTI_EVENTS.CLICK.NAVIGATION.PAGE_CHANGE,
      }),
    )
    setChangeApplicationFields(true)
    setApplicationPageFields(applicationPage2Fields)
  }

  const handleAddressNormalization = (address: any) => {
    clientLogger.info(
      'application page 2 address normalization - Application ID:',
      data?.applicationMetaData?.applicationId,
    )
    const addressMapping = {
      addressLine1: address?.addressLine1,
      addressLine2: address?.addressLine2,
      adminArea2: address?.adminArea2,
      adminArea1: address?.adminArea1,
      postalCode: address?.postalCode,
      countryCode: address?.countryCode || 'US',
    }
    generateNormalizedAddress({
      variables: {
        addressInput: {
          address: addressMapping,
        },
      },
    })
  }

  const initialSubmitActionLogs = () => {
    trackClick(
      merge(fptiData, {
        component: 'applicationPage2_continue_button',
        link: 'applicationPage2_continue_button',
        applicationId: data?.applicationMetaData?.applicationId,
        event_name: FPTI_EVENTS.CLICK.PAGE_CONTINUE.PAGE2,
      }),
    )

    scrollToTopOnContinue()

    clientLogger.info(
      'application page 2 submitted - Application ID:',
      data?.applicationMetaData?.applicationId,
    )
    clientLogger.info(
      'Legal bussiness name for the application',
      data?.applicationMetaData?.legalBusinessName,
    )
    if (isPaypalApplication) {
      clientLogger.info(
        'paypal users encrypted account number',
        serverData?.user?.encryptedAccountNumber,
      )
    } else if (renewalToken) {
      clientLogger.info('renewal user renewal token', renewalToken)
    }
  }

  const handleApplicationSubmit = async (values: any) => {
    initialSubmitActionLogs()

    const applicationValues = getApplicationFieldsData(
      applicationPage2Fields,
      cachedApplicationData,
      values,
    )

    //sanitize the input values to prevent XSS
    santizeInputFields(applicationValues)

    const address = {
      addressLine1: applicationValues?.homeAddress1,
      addressLine2: applicationValues?.homeAddress2,
      adminArea1: applicationValues?.state,
      adminArea2: applicationValues?.city,
      postalCode: applicationValues?.zip,
      countryCode: applicationValues?.countryCode || 'US',
    }
    handleAddressNormalization(address)
    setUserAddress(address)
    setApplicationFormData(applicationValues)
  }

  useEffect(() => {
    const worldReadyLocale = serverData.locality.locale
    const worldReady = new WorldReady(worldReadyLocale)
    const fieldsToUpdate: Record<string, string> = {}
    if (applicationType !== 'paypal' && applicationType !== 'app') {

      const addressFormatter = new AddressFormat(worldReady, {
        addressRegion: 'US',
      })
      const addressKeyMap: Record<string, string> = {
        addressLine1: 'homeAddress1',
        addressLine2: 'homeAddress2',
        adminArea1: 'state',
        adminArea2: 'city',
        postalCode: 'zip',
      }
      const isAddressValid = addressFormatter.validate({
        addressLine1: cachedApplicationData?.homeAddress1 || '',
        addressLine2: cachedApplicationData?.homeAddress2 || '',
        adminArea1: cachedApplicationData?.state || '',
        adminArea2: cachedApplicationData?.city || '',
        postalCode: cachedApplicationData?.zip || '',
        countryCode: cachedApplicationData?.countryCode ?? 'US',
      })

      if (isAddressValid?.errors?.length > 0) {
        clientLogger.info(
          'Clearing prepopulating invalid home Address data for renewal application - Application ID:',
          data?.applicationMetaData?.applicationId,
        )
        setChangeApplicationFields(true)
        setApplicationPageFields(applicationPage2Fields)
        isAddressValid?.errors.forEach((error) => {
          const key = addressKeyMap[error.element]
          if (key) {
            fieldsToUpdate[key] = ''
          }
        })
      }
      updateApplicationCachedData(
        {
          ...fieldsToUpdate,
        },
        cachedApplicationData,
      )
    }
  }, [])

  const constructAddressFields = () => {
    const addressFields = {
      addressLine1: cachedApplicationData.homeAddress1,
      addressLine2: cachedApplicationData.homeAddress2,
      addressLine3: `${cachedApplicationData.city}, ${cachedApplicationData.state} ${cachedApplicationData.zip}`,
    }

    return addressFields
  }

  const focusOnErrors = createDecorator()

  const handleConfirmAddress = (isRecommended) => {
    clientLogger.info(
      `application page 2 submitted with the ${isRecommended} address - Application ID:`,
      data?.applicationMetaData?.applicationId,
    )
    setIsAddressNormalizationDialog(false)
    if (isRecommended === addressDialogConstants.enteredAddress) {
      updateApplicationCachedData(applicationFormData, cachedApplicationData)
      updateApplication({
        variables: {
          updateInput: updateApplicationPayloadGeneratorPage2(
            applicationFormData,
            data.applicationMetaData.applicationId,
          ),
        },
      })
    } else {
      const applicationData = {
        ...applicationFormData,
        homeAddress1: suggestedAddress?.address?.addressLine1,
        homeAddress2: suggestedAddress?.address?.addressLine2,
        state: suggestedAddress?.address?.adminArea1,
        city: suggestedAddress?.address?.adminArea2,
        zip: suggestedAddress?.address?.postalCode,
        country: suggestedAddress?.address?.countryCode,
      }
      updateApplicationCachedData(applicationData, cachedApplicationData)
      updateApplication({
        variables: {
          updateInput: updateApplicationPayloadGeneratorPage2(
            applicationData,
            data.applicationMetaData.applicationId,
          ),
        },
      })
    }
  }

  const renderAddressNormalizationSpinner = () => (
    <div className="normalizeSpinnerWrapper">
      <div className="spinner">
        <SpinnerAdapter />
      </div>
    </div>
  )

  return (
    <>
      {loading || creditApplicationLoading ? (
        <div className="spinner">
          <SpinnerAdapter />
        </div>
      ) : (
        <>
          {data?.eligibilityData?.preApproved && (
            <PreApprovalBanner
              approvalCategory={data?.eligibilityData?.category}
              expiryDate={data?.eligibilityData?.expiryDate}
            />
          )}
        <Form onSubmit={handleApplicationSubmit} decorators={[focusOnErrors]}>
          {({ handleSubmit, hasValidationErrors }) => (
            <div className="applicationFormContainer">
              <form onSubmit={handleSubmit}>
                <div className="applicationTextSection">
                  <Row>
                    <Col form="full">
                      <HeadingText size="sm">
                        {getLocalizedText(
                          'application.applicationPages.applicationPage2.headingText',
                        )}
                      </HeadingText>
                    </Col>
                  </Row>
                  <Row>
                    <Col form="full">
                      <BodyText>
                        {getLocalizedText(
                          'application.applicationPages.applicationPage2.checkEligibilityText',
                        )}
                      </BodyText>
                    </Col>
                  </Row>
                </div>
                <div className="applicationFields">
                  <Row>
                    <Col form="full">
                      <BodyText strong data-dd-privacy="mask">
                        {`${cachedApplicationData?.firstName} ${cachedApplicationData?.lastName}`}
                      </BodyText>
                    </Col>
                  </Row>
                  {!changeApplicationFields && (
                    <AddressCard
                      addressFields={constructAddressFields()}
                      handleButtonClick={handleAddressOnChange}
                      addressType="Home address"
                    />
                  )}
                  {applicationPageFields.map((applicationField, index) => {
                    if (Array.isArray(applicationField)) {
                      return (
                        <Row key={`applicationFieldsRow${index}`}>
                          {applicationField.map((field) => {
                            const { name, ...props } = field
                            return (
                              <Col key={name} form="half">
                                <Field
                                  name={name}
                                  {...props}
                                  initialValue={
                                    cachedApplicationData?.[name] || null
                                  }
                                />
                              </Col>
                            )
                          })}
                        </Row>
                      )
                    }
                    const { name, ...props } = applicationField
                    return (
                      <Row key={name}>
                        <Col form="full">
                          <Field
                            name={name}
                            {...props}
                            initialValue={cachedApplicationData?.[name] || null}
                          />
                        </Col>
                      </Row>
                    )
                  })}
                </div>
                <Button
                  className="ctaSection"
                  size="lg"
                  onClick={() => scrollOnErrors(hasValidationErrors)}
                  type="submit"
                >
                  {getLocalizedText(
                    'application.applicationPages.applicationPage2.continueButtonText',
                  )}
                </Button>
              </form>
              <InfoText />
            </div>
          )}
        </Form>
        </>
      )}
      {addressNormalizationLoading && renderAddressNormalizationSpinner()}
      <AddressConfirmationDialog
        isAddressDialogOpen={isAddressNormalizationDialog}
        setIsAddressDialogOpen={setIsAddressNormalizationDialog}
        confirmAddress={handleConfirmAddress}
        status={suggestedAddress?.status}
        suggestedAddress={suggestedAddress}
        userAddress={userAddress}
        applicationId={data.applicationMetaData.applicationId}
      />
    </>
  )
}

export default ApplicationPage2
